import { ArrowRight, Check } from 'lucide-react';
import { Link } from 'react-router';

import { findMeasure, formatMeasure, openings, tileMeasureIds } from './kaiCoachContent';

import styles from './KaiCoachEvidence.module.css';

const tiles = tileMeasureIds.map(findMeasure);

/** The results beyond the chart: per-paper figures, the spread, two openings,
 *  and how to read it all. */
export function KaiCoachEvidence() {
  return (
    <div className={styles.evidence}>
      <ul className={styles.tiles}>
        {tiles.map((measure) => (
          <li className={styles.tile} key={measure.id}>
            <p className={styles.tileLabel}>{measure.label}</p>
            <p className={styles.tileValues}>
              <span className={styles.from}>{formatMeasure(measure, measure.before)}</span>
              <span aria-hidden="true" className={styles.arrow}>
                →
              </span>
              <span className={styles.srOnly}> in 2025, </span>
              <span className={styles.to}>{formatMeasure(measure, measure.after)}</span>
              <span className={styles.srOnly}> in 2026</span>
            </p>
            {measure.heldUp ? (
              <p className={styles.badge}>
                <Check aria-hidden="true" size={12} />
                Held up under multiple-comparison correction
              </p>
            ) : null}
          </li>
        ))}
      </ul>

      <div className={styles.callout}>
        <div>
          <h3 className={styles.heading}>The gain reached both ends of the class.</h3>
          <p className={styles.body}>
            Papers with no developed argument fell by more than half. Papers with four or more
            roughly tripled. The spread barely changed: the whole class shifted, rather than the
            strongest students pulling away.
          </p>
        </div>
        <div className={styles.calloutStat}>
          <p className={styles.calloutValue}>5 of 5</p>
          <p className={styles.calloutText}>
            identical final-exam questions favored the coached year, across 143 student answers.
          </p>
        </div>
      </div>

      <div className={styles.openings}>
        <h3 className={styles.heading}>The same exam question, one year apart</h3>
        <p className={styles.body}>
          Why did witch hunts rise as heresy trials declined? The opening lines of two answers:
        </p>
        <div className={styles.openingGrid}>
          {openings.map((opening, index) => (
            <figure
              className={index === 1 ? `${styles.opening} ${styles.openingAfter}` : styles.opening}
              key={opening.year}
            >
              <p className={styles.year}>{opening.year}</p>
              <blockquote className={styles.quote}>{opening.quote}</blockquote>
              <figcaption className={styles.openingCaption}>{opening.caption}</figcaption>
            </figure>
          ))}
        </div>
      </div>

      <div className={styles.method} id="method">
        <h3 className={styles.methodTitle}>How to read these results</h3>
        <p className={styles.methodText}>
          This is a comparison of two cohorts of one course, not a randomized trial, and the
          cohorts differ in year and size. Of the content measures, the rise in mechanism
          reasoning on the final exam is the one that held up after correcting for multiple
          comparisons. The other figures are observed differences between the two years.
        </p>
        <Link className={styles.methodLink} to="/contact">
          Request the full pilot report
          <ArrowRight aria-hidden="true" size={16} />
        </Link>
      </div>
    </div>
  );
}
